import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '../../../shared/components/Alert';
import { Button } from '../../../shared/components/Button';
import { PageContainer } from '../../../shared/components/PageContainer';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { maskCPF, maskPhone, validateCPF } from '../../../shared/utils/validators';
import { BondPayload, PersonalData, corpoDoErro, mensagemDeErro } from '../api';
import { BondFields } from '../components/BondFields';
import { cleanBondPayload, validateBondForm } from '../components/format';
import { PersonalDataFields } from '../components/PersonalDataFields';
import { useCriarColaborador, useDuplicidades } from '../hooks';

interface Identificacao {
  name: string;
  cpf: string;
  rg: string;
  email: string;
  phone: string;
}

const REASON_LABELS: Record<string, string> = {
  cpf: 'mesmo CPF',
  nome_nascimento: 'mesmo nome e data de nascimento',
  email: 'mesmo e-mail',
  telefone: 'mesmo telefone',
};

function Secao({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-lg border border-gray-200 bg-white p-5">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {children}
    </section>
  );
}

/**
 * Novo colaborador (US-GP-001, fluxo principal). A duplicidade é checada
 * enquanto o usuário digita; CPF igual impede o cadastro e leva à pessoa
 * existente para criar um novo vínculo (RN-GP-001-007).
 */
export default function NovoColaboradorPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const criar = useCriarColaborador();

  const [ident, setIdent] = useState<Identificacao>({ name: '', cpf: '', rg: '', email: '', phone: '' });
  const [pessoal, setPessoal] = useState<PersonalData>({ birthCountry: 'Brasil', nationality: 'Brasileira' });
  const [bond, setBond] = useState<Partial<BondPayload>>({ employmentType: 'clt', contractKind: 'indeterminado' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [checkParams, setCheckParams] = useState<{ cpf?: string; name?: string; birthDate?: string; email?: string }>({});

  // A checagem roda com debounce sobre os campos que identificam a pessoa.
  useEffect(() => {
    const t = setTimeout(() => {
      setCheckParams({
        cpf: validateCPF(ident.cpf) ? ident.cpf.replace(/\D/g, '') : undefined,
        name: ident.name.trim() || undefined,
        birthDate: pessoal.birthDate || undefined,
        email: ident.email.includes('@') ? ident.email.trim() : undefined,
      });
    }, 400);
    return () => clearTimeout(t);
  }, [ident.cpf, ident.name, ident.email, pessoal.birthDate]);

  const temChave = !!(checkParams.cpf || checkParams.email || (checkParams.name && checkParams.birthDate));
  const duplicidades = useDuplicidades(checkParams, temChave);
  const matches = duplicidades.data ?? [];
  const mesmoCpf = matches.find((m) => m.reasons.includes('cpf'));

  function validar(): boolean {
    const found: Record<string, string> = {};
    if (!ident.name.trim()) found.name = 'Nome completo é obrigatório.';
    if (!validateCPF(ident.cpf)) found.cpf = 'CPF inválido.';
    if (!ident.rg.trim()) found.rg = 'RG é obrigatório.';
    if (!ident.email.includes('@')) found.email = 'E-mail inválido.';
    if (ident.phone.replace(/\D/g, '').length < 10) found.phone = 'Telefone inválido.';
    if (pessoal.birthDate && pessoal.birthDate > new Date().toISOString().slice(0, 10)) {
      found.birthDate = 'A data de nascimento não pode ser futura.';
    }
    Object.assign(found, validateBondForm(bond));
    setErrors(found);
    return Object.keys(found).length === 0;
  }

  async function salvar() {
    setSubmitError(null);
    if (!validar() || mesmoCpf) return;
    try {
      const criado = await criar.mutateAsync({
        ...pessoal,
        name: ident.name.trim(),
        cpf: ident.cpf,
        rg: ident.rg.trim(),
        email: ident.email.trim(),
        phone: ident.phone,
        bond: cleanBondPayload(bond as BondPayload),
      });
      toast.success(`Colaborador cadastrado com a matrícula ${criado.bonds[0]?.internalRegistration}.`);
      if (criado.welcomeEmailSent === false) {
        toast.warning('O e-mail de acesso não foi enviado. Repasse o link de primeiro acesso ao colaborador.', {
          duration: 15000,
        });
      }
      navigate(`/pessoas/colaboradores/${criado.id}`);
    } catch (err) {
      const corpo = corpoDoErro(err);
      setSubmitError(mensagemDeErro(err, 'Não foi possível cadastrar o colaborador.'));
      if (corpo.personId) setErrors((e) => ({ ...e, existente: corpo.personId as string }));
    }
  }

  return (
    <PageContainer
      title="Novo colaborador"
      subtitle="A pessoa é cadastrada uma única vez; cada contratação gera um vínculo com matrícula própria."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Colaboradores', to: '/pessoas/colaboradores' }, { label: 'Novo' }]}
    >
      <div className="space-y-6">
        <Secao title="Identificação">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput label="Nome completo" value={ident.name} error={errors.name}
              onChange={(e) => setIdent((i) => ({ ...i, name: e.target.value }))} />
            <TextInput label="CPF" value={ident.cpf} error={errors.cpf}
              onChange={(e) => setIdent((i) => ({ ...i, cpf: maskCPF(e.target.value) }))} />
            <TextInput label="RG" value={ident.rg} error={errors.rg}
              onChange={(e) => setIdent((i) => ({ ...i, rg: e.target.value }))} />
            <TextInput label="E-mail" type="email" value={ident.email} error={errors.email}
              onChange={(e) => setIdent((i) => ({ ...i, email: e.target.value }))} />
            <TextInput label="Telefone" value={ident.phone} error={errors.phone}
              onChange={(e) => setIdent((i) => ({ ...i, phone: maskPhone(e.target.value) }))} />
          </div>

          {mesmoCpf ? (
            <Alert variant="error" title="Já existe uma pessoa com este CPF">
              <p>
                {mesmoCpf.name} ({mesmoCpf.cpf}). Não é possível cadastrar a mesma pessoa duas vezes.
              </p>
              <div className="mt-2 flex gap-3">
                <Link className="font-medium underline" to={`/pessoas/colaboradores/${mesmoCpf.id}`}>Abrir cadastro</Link>
                <Link className="font-medium underline" to={`/pessoas/colaboradores/${mesmoCpf.id}?novoVinculo=1`}>
                  Criar novo vínculo
                </Link>
              </div>
            </Alert>
          ) : matches.length > 0 ? (
            <Alert variant="warning" title="Possíveis duplicidades">
              <ul className="list-disc pl-5">
                {matches.map((m) => (
                  <li key={m.id}>
                    <Link className="underline" to={`/pessoas/colaboradores/${m.id}`}>{m.name}</Link>
                    {' — '}{m.reasons.map((r) => REASON_LABELS[r] ?? r).join(', ')}
                  </li>
                ))}
              </ul>
              <p className="mt-1">Confira antes de continuar.</p>
            </Alert>
          ) : null}
        </Secao>

        <Secao title="Dados pessoais">
          <PersonalDataFields value={pessoal} errors={errors} onChange={(patch) => setPessoal((p) => ({ ...p, ...patch }))} />
        </Secao>

        <Secao title="Vínculo">
          <BondFields value={bond} errors={errors} onChange={(patch) => setBond((b) => ({ ...b, ...patch }))} />
          <p className="text-xs text-gray-500">
            Sem matrícula informada, o sistema gera a próxima da empresa. O vínculo nasce em “Admissão em Preparação”.
          </p>
        </Secao>

        {submitError && (
          <Alert variant="error">
            {submitError}
            {errors.existente && (
              <Link className="ml-2 font-medium underline" to={`/pessoas/colaboradores/${errors.existente}`}>Abrir cadastro existente</Link>
            )}
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate('/pessoas/colaboradores')}>Cancelar</Button>
          <Button onClick={salvar} loading={criar.isPending} disabled={!!mesmoCpf}>Cadastrar colaborador</Button>
        </div>
      </div>
    </PageContainer>
  );
}
